import { Button, TextArea } from '@calizahq/react-components-v2'
import { Form, Formik } from 'formik'
import React, { useState } from 'react'

import { InputFile } from '@/components'
import { useAlertContext } from '@/context'
import { HomeQualityCheckMutations } from '@/graphql/mutations/homeQualityChecks.mutations'
import { useMobile } from '@/hooks'
import { useGeolocation } from '@/hooks/useGeolocation'
import { useGraphqlMutation } from '@/hooks/useGraphql'
import { HomeQualityCheck } from '@/types/HomeQualityCheck'

import { formSchema, FormValuesType } from './UpdateInspection.util'

type Props = {
  homeQualityCheck: HomeQualityCheck
  refetchOnSuccess: () => void
  toggle: () => void
}

export const UpdateHomeQualityCheckInspection = ({ homeQualityCheck, refetchOnSuccess, toggle }: Props) => {
  const { isTablet } = useMobile()
  const { setAlert } = useAlertContext()
  const [isCapturing, setIsCapturing] = useState(false)
  const { captureGeolocation } = useGeolocation()

  const {
    mutate,
    isError,
    isPending: isMutationLoading,
  } = useGraphqlMutation(HomeQualityCheckMutations.updateHomeQualityCheck(), {
    onSuccess: () => {
      refetchOnSuccess()
      toggle()
      setAlert('Home Quality Check updated.', 'success')
    },
  })

  const handleSubmit = async (values: FormValuesType) => {
    if (isCapturing) return
    setIsCapturing(true)
    try {
      const { attachments, notes } = values
      const geolocation = await captureGeolocation()
      mutate({
        homeQualityCheck: {
          homeQualityCheckId: homeQualityCheck.id,
          notes,
        },
        attachments,
        geolocation,
      })
    } finally {
      setIsCapturing(false)
    }
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        notes: homeQualityCheck.notes || '',
      }}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validationSchema={formSchema}
    >
      {() => {
        return (
          <Form className="flex w-full flex-col flex-wrap px-6 md:max-w-120">
            <TextArea className="mb-4 w-full" label="Notes" name="notes" />

            <InputFile
              accept={{
                'image/jpeg': [],
                'image/png': [],
              }}
              className="mb-4"
              gridClassName="grid-cols-3 md:grid-cols-5"
              id="attachments"
              isPreview={true}
              maxFiles={6}
              maxSize={15000000}
              name="attachments"
            />

            {isError && (
              <p className=" mb-4 text-p-sm text-text-semantic-error">
                Unable to update the Home Quality Check. Please try again later.
              </p>
            )}
            <div className="flex flex-col-reverse justify-between md:flex-row">
              <Button
                className="w-full bg-action-secondary md:flex md:w-auto"
                onClick={toggle}
                size={isTablet ? 'medium' : 'small'}
                variant="secondary"
              >
                Close
              </Button>
              <Button
                className="mb-2 w-full bg-text-semantic-success md:mb-0 md:w-auto"
                disabled={isCapturing || isMutationLoading}
                isLoading={isCapturing || isMutationLoading}
                size={isTablet ? 'medium' : 'small'}
                type="submit"
              >
                Submit
              </Button>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}
